// apps/web/src/pages/ProfilePage.tsx
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useForm, useFormContext, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Upload,
  Camera,
  RefreshCw,
  Save,
  CheckCircle,
  Image,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  User,
  ArrowLeft,
  Info,
  Briefcase,
  HandHelping,
  CreditCard,
  Shield,
} from 'lucide-react';
import type { OcrResult, ProfileSubmit } from '@hc-alumni/shared/validation';
import {
  profileSubmitSchema,
  graduateYearOptions,
  formatGraduateYearOption,
  toRocYear,
} from '@hc-alumni/shared/validation';
import { compressCardImage, formatBytes } from '../lib/imageCompress';
import ImageLightbox, { ZoomableImage } from '../components/ImageLightbox';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';

interface FieldProps {
  label: string;
  name: keyof ProfileSubmit;
  type?: string;
  min?: number;
  max?: number;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  error?: { message?: string } | undefined;
}

function Field({
  label,
  name,
  type = 'text',
  min,
  max,
  required,
  hint,
  placeholder,
  error,
}: FieldProps) {
  const form = useFormContext();
  return (
    <div>
      <label className="label flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <input
        {...form.register(name)}
        type={type}
        min={min}
        max={max}
        placeholder={placeholder}
        className={`input ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${name}-error` : hint ? `${name}-hint` : undefined}
      />
      {hint && !error && (
        <p id={`${name}-hint`} className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${name}-error`} className="text-red-500 text-sm mt-1" role="alert">
          {error.message ?? '欄位有誤'}
        </p>
      )}
    </div>
  );
}

function GraduateYearField({
  error,
  isFaculty,
}: {
  error?: { message?: string };
  isFaculty?: boolean;
}) {
  const form = useFormContext<ProfileSubmit>();
  const years = graduateYearOptions();
  const value = form.watch('graduateYear');
  const ad =
    value != null && value !== ('' as unknown) && Number.isFinite(Number(value))
      ? Number(value)
      : null;
  const roc = ad != null ? toRocYear(ad) : null;

  return (
    <div className={isFaculty ? 'opacity-90' : undefined}>
      <label className="label flex items-center gap-1" htmlFor="graduateYear">
        畢業年份
        {isFaculty && (
          <span className="text-xs font-normal text-gray-400">（師長／教職員可不填）</span>
        )}
      </label>
      <select
        id="graduateYear"
        className={`input ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
        value={ad == null ? '' : String(ad)}
        onChange={(e) => {
          const v = e.target.value;
          form.setValue('graduateYear', v === '' ? null : Number(v), {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? 'graduateYear-error' : 'graduateYear-hint'}
      >
        <option value="">{isFaculty ? '不適用／不填' : '不確定／稍後再填'}</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {formatGraduateYearOption(y)}
          </option>
        ))}
      </select>
      {ad != null && roc != null && (
        <p className="mt-2 text-sm text-alumni-600 font-medium">
          已選：西元 {ad} 年 ＝ 民國 {roc} 年
        </p>
      )}
      <p id="graduateYear-hint" className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {isFaculty
          ? '師長／教職員身分可不填畢業年；若同時為校友畢業生也可選填。'
          : '和春技術學院期間：西元 1989–2023（民國 78–112）。'}
      </p>
      {error && (
        <p id="graduateYear-error" className="text-red-500 text-sm mt-1" role="alert">
          {error.message ?? '畢業年份格式不正確'}
        </p>
      )}
    </div>
  );
}

function SectionHeader({
  step,
  title,
  subtitle,
  icon: Icon,
}: {
  step: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-alumni-100 dark:bg-alumni-900/40 text-alumni-700 dark:text-alumni-200">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold tracking-wide text-alumni-600 uppercase">{step}</p>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{subtitle}</p>
      </div>
    </div>
  );
}

type WizardPhase = 'personal' | 'card' | 'saved';

export default function ProfilePage() {
  const { userId: routeUserId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();
  const authUser = useAuth((s) => s.user);
  const queryClient = useQueryClient();

  /** 代編僅主管理者 */
  const isAdminEdit = !!(routeUserId && authUser?.isOwner && routeUserId !== authUser?.id);
  const targetUserId = isAdminEdit ? routeUserId! : authUser?.id || '';

  const [phase, setPhase] = useState<WizardPhase>('personal');
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [lineAvatarUrl, setLineAvatarUrl] = useState<string | null>(null);
  const [ocrData, setOcrData] = useState<OcrResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [showOcrRaw, setShowOcrRaw] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; alt?: string } | null>(null);
  const [hydrated, setHydrated] = useState(false);
  /** false = 首次導覽；true = 完整編輯介面 */
  const [isReturningEdit, setIsReturningEdit] = useState(false);
  const [lastSaveNeedsReview, setLastSaveNeedsReview] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<ProfileSubmit>({
    resolver: zodResolver(profileSubmitSchema),
    defaultValues: {
      name: '',
      department: '',
      graduateYear: null,
      currentCompany: '',
      currentTitle: '',
      phone: '',
      email: '',
      lineId: '',
      bio: '',
      industry: '',
      lookingFor: '',
      isFaculty: false,
      isPublic: true,
      avatarUrl: null,
      cardImageUrl: null,
      coverImageUrl: null,
      cardOcrText: null,
    },
  });

  const {
    data: myProfile,
    isLoading: profileLoading,
    isError: profileError,
    error: profileErr,
  } = useQuery({
    queryKey: isAdminEdit ? ['profile', 'admin', routeUserId] : ['profile', 'me'],
    queryFn: () =>
      isAdminEdit ? api.admin.getAlumni(routeUserId!) : api.profile.getMe(),
    enabled: isAdminEdit ? !!routeUserId && !!authUser?.isOwner : !!authUser?.id,
  });

  useEffect(() => {
    if (!myProfile || hydrated) return;

    const values: ProfileSubmit = {
      name: myProfile.name || '',
      department: myProfile.department || '',
      graduateYear: myProfile.graduateYear ?? null,
      currentCompany: myProfile.currentCompany || '',
      currentTitle: myProfile.currentTitle || '',
      phone: myProfile.phone || '',
      email: myProfile.email || '',
      lineId: myProfile.lineId || '',
      bio: myProfile.bio || '',
      industry: (myProfile as { industry?: string | null }).industry || '',
      lookingFor: (myProfile as { lookingFor?: string | null }).lookingFor || '',
      isFaculty: !!myProfile.isFaculty,
      isPublic: myProfile.isPublic !== false,
      avatarUrl: myProfile.avatarUrl || null,
      cardImageUrl: myProfile.cardImageUrl || null,
      coverImageUrl: myProfile.coverImageUrl || null,
      cardOcrText: myProfile.cardOcrText || null,
    };
    form.reset(values);

    if (myProfile.cardImageUrl) setImgUrl(myProfile.cardImageUrl);
    if (myProfile.coverImageUrl) setCoverUrl(myProfile.coverImageUrl);
    const av = myProfile.avatarUrl || null;
    setAvatarUrl(av);
    if (av && /line-scdn\.net|profile\.line-cdn\.net/i.test(av)) {
      setLineAvatarUrl(av);
    }
    if (myProfile.cardOcrText) {
      setOcrData({ text: myProfile.cardOcrText });
    }

    // 曾填過名片／公司／科系／產業／需求 → 編輯介面；否則首次導覽
    const returning = !!(
      myProfile.cardImageUrl ||
      myProfile.department ||
      myProfile.currentCompany ||
      myProfile.bio ||
      (myProfile as { industry?: string | null }).industry ||
      (myProfile as { lookingFor?: string | null }).lookingFor ||
      myProfile.isVerified
    );
    setIsReturningEdit(returning || isAdminEdit);
    setPhase('personal');
    setLastSaveNeedsReview(!myProfile.isVerified);
    setHydrated(true);
  }, [myProfile, hydrated, form, isAdminEdit]);

  const forUserIdOpt = useMemo(
    () => (isAdminEdit ? targetUserId : undefined),
    [isAdminEdit, targetUserId]
  );

  const getUploadUrl = useMutation({
    mutationFn: ({ filename, contentType }: { filename: string; contentType: string }) =>
      api.card.getUploadUrl(filename, contentType, forUserIdOpt),
  });

  const uploadToR2 = useMutation({
    mutationFn: async ({
      url,
      fields,
      file,
    }: {
      url: string;
      fields: Record<string, string>;
      file: File;
    }) => {
      const formData = new FormData();
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
      formData.append('file', file);
      if (forUserIdOpt) formData.append('forUserId', forUserIdOpt);
      const res = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(
          typeof err === 'object' && err && 'error' in err ? String(err.error) : 'Upload failed'
        );
      }
      return res.json() as Promise<{ publicUrl?: string; key?: string }>;
    },
  });

  const runOcr = useMutation({
    mutationFn: (publicUrl: string) => api.card.ocr(publicUrl, forUserIdOpt),
  });

  const saveCard = useMutation({
    mutationFn: async (data: ProfileSubmit) => {
      if (isAdminEdit) {
        return api.admin.updateAlumni(routeUserId!, {
          ...data,
          keepVerified: true,
        });
      }
      return api.card.save(data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['directory'] });
      queryClient.invalidateQueries({ queryKey: ['admin-alumni'] });
      setIsReturningEdit(true);
      setPhase('saved');
      const needsReview =
        !isAdminEdit &&
        (res as { needsReview?: boolean })?.needsReview === true;
      setLastSaveNeedsReview(needsReview);
      toast.success(
        isAdminEdit
          ? '已儲存校友資料'
          : needsReview
            ? '資料已儲存，審核通過後會出現在校友名冊'
            : '資料已儲存，謝謝'
      );
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : '儲存失敗'),
  });

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 25 * 1024 * 1024) {
        toast.error('原始檔案過大（超過 25MB），請改用較小的照片');
        return;
      }

      const nameLower = (file.name || '').toLowerCase();
      let contentType = file.type;
      if (!contentType || contentType === 'application/octet-stream') {
        if (nameLower.endsWith('.png')) contentType = 'image/png';
        else if (nameLower.endsWith('.webp')) contentType = 'image/webp';
        else if (
          nameLower.endsWith('.jpg') ||
          nameLower.endsWith('.jpeg') ||
          nameLower.endsWith('.jfif')
        )
          contentType = 'image/jpeg';
        else contentType = 'image/jpeg';
      }
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(contentType)) {
        toast.error('僅支援 JPG、PNG、WebP 格式');
        return;
      }

      setUploading(true);
      try {
        toast.message('正在壓縮圖片…');
        const compressed = await compressCardImage(file, {
          maxEdge: 1600,
          maxBytes: 800 * 1024,
          quality: 0.85,
        });
        const uploadFile = compressed.file;
        contentType = 'image/jpeg';

        if (compressed.changed) {
          toast.success(
            `已自動壓縮：${formatBytes(compressed.originalBytes)} → ${formatBytes(compressed.compressedBytes)}（${compressed.width}×${compressed.height}）`
          );
        }

        const { uploadUrl, fields, publicUrl } = await getUploadUrl.mutateAsync({
          filename: uploadFile.name,
          contentType,
        });

        const uploaded = await uploadToR2.mutateAsync({
          url: uploadUrl,
          fields: { ...fields, contentType },
          file: uploadFile,
        });
        const finalUrl = uploaded.publicUrl || publicUrl;

        setImgUrl(finalUrl);
        form.setValue('cardImageUrl', finalUrl);

        setOcrLoading(true);
        try {
          const ocr = await runOcr.mutateAsync(finalUrl);
          setOcrData(ocr);
          if (ocr.parsed) {
            Object.entries(ocr.parsed).forEach(([key, value]) => {
              if (value && !form.getValues(key as keyof ProfileSubmit)) {
                form.setValue(key as keyof ProfileSubmit, value as never);
              }
            });
          }
          toast.success('上傳成功，請確認下方欄位');
        } catch (ocrErr) {
          console.warn('OCR skipped', ocrErr);
          toast.success('上傳成功，請確認下方欄位');
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : '上傳失敗，請重試';
        toast.error(msg);
        console.error(err);
      } finally {
        setUploading(false);
        setOcrLoading(false);
        e.target.value = '';
      }
    },
    [form, getUploadUrl, uploadToR2, runOcr]
  );

  const handleCoverChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 25 * 1024 * 1024) {
        toast.error('原始檔案過大（超過 25MB），請改用較小的照片');
        return;
      }

      const nameLower = (file.name || '').toLowerCase();
      let contentType = file.type;
      if (!contentType || contentType === 'application/octet-stream') {
        if (nameLower.endsWith('.png')) contentType = 'image/png';
        else if (nameLower.endsWith('.webp')) contentType = 'image/webp';
        else contentType = 'image/jpeg';
      }
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(contentType)) {
        toast.error('僅支援 JPG、PNG、WebP 格式');
        return;
      }

      setCoverUploading(true);
      try {
        toast.message('正在壓縮封面…');
        const compressed = await compressCardImage(file, {
          maxEdge: 1920,
          maxBytes: 900 * 1024,
          quality: 0.85,
        });
        const uploadFile = compressed.file;
        contentType = 'image/jpeg';

        const { uploadUrl, fields, publicUrl } = await getUploadUrl.mutateAsync({
          filename: uploadFile.name || 'cover.jpg',
          contentType,
        });

        const uploaded = await uploadToR2.mutateAsync({
          url: uploadUrl,
          fields: { ...fields, contentType },
          file: uploadFile,
        });
        const finalUrl = uploaded.publicUrl || publicUrl;

        setCoverUrl(finalUrl);
        form.setValue('coverImageUrl', finalUrl, { shouldDirty: true });
        toast.success('封面已上傳，請記得按儲存');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '封面上傳失敗');
        console.error(err);
      } finally {
        setCoverUploading(false);
        e.target.value = '';
      }
    },
    [form, getUploadUrl, uploadToR2]
  );

  const clearCover = () => {
    setCoverUrl(null);
    form.setValue('coverImageUrl', null, { shouldDirty: true });
    toast.message('已改回預設封面，請記得按儲存');
  };

  const handleAvatarChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 25 * 1024 * 1024) {
        toast.error('原始檔案過大（超過 25MB），請改用較小的照片');
        return;
      }

      const nameLower = (file.name || '').toLowerCase();
      let contentType = file.type;
      if (!contentType || contentType === 'application/octet-stream') {
        if (nameLower.endsWith('.png')) contentType = 'image/png';
        else if (nameLower.endsWith('.webp')) contentType = 'image/webp';
        else contentType = 'image/jpeg';
      }
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(contentType)) {
        toast.error('僅支援 JPG、PNG、WebP 格式');
        return;
      }

      setAvatarUploading(true);
      try {
        toast.message('正在壓縮頭像…');
        const compressed = await compressCardImage(file, {
          maxEdge: 800,
          maxBytes: 400 * 1024,
          quality: 0.88,
        });
        const uploadFile = compressed.file;
        contentType = 'image/jpeg';

        const { uploadUrl, fields, publicUrl } = await getUploadUrl.mutateAsync({
          filename: uploadFile.name || 'avatar.jpg',
          contentType,
        });

        const uploaded = await uploadToR2.mutateAsync({
          url: uploadUrl,
          fields: { ...fields, contentType },
          file: uploadFile,
        });
        const finalUrl = uploaded.publicUrl || publicUrl;

        setAvatarUrl(finalUrl);
        form.setValue('avatarUrl', finalUrl, { shouldDirty: true });
        toast.success('頭像已上傳，請記得按儲存');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '頭像上傳失敗');
        console.error(err);
      } finally {
        setAvatarUploading(false);
        e.target.value = '';
      }
    },
    [form, getUploadUrl, uploadToR2]
  );

  const clearAvatar = () => {
    setAvatarUrl(null);
    form.setValue('avatarUrl', null, { shouldDirty: true });
    toast.message('已清除頭像，請記得按儲存');
  };

  const restoreLineAvatar = () => {
    if (!lineAvatarUrl) {
      toast.message('目前沒有可還原的 LINE 頭像');
      return;
    }
    setAvatarUrl(lineAvatarUrl);
    form.setValue('avatarUrl', lineAvatarUrl, { shouldDirty: true });
    toast.message('已還原 LINE 頭像，請記得按儲存');
  };

  const buildPayload = (data: ProfileSubmit): ProfileSubmit => ({
    ...data,
    name: (data.name || '').trim(),
    department: data.department || null,
    graduateYear: (() => {
      const raw = data.graduateYear as unknown;
      if (raw === undefined || raw === null || raw === '') return null;
      let n = Number(raw);
      if (!Number.isFinite(n)) return null;
      if (n >= 1 && n <= 200) n = n + 1911;
      return Math.trunc(n);
    })(),
    currentCompany: data.currentCompany || null,
    currentTitle: data.currentTitle || null,
    phone: data.phone || null,
    email: data.email || null,
    lineId: data.lineId || null,
    bio: data.bio || null,
    industry: data.industry || null,
    lookingFor: data.lookingFor || null,
    isFaculty: !!data.isFaculty,
    isPublic: data.isPublic !== false,
    avatarUrl: data.avatarUrl !== undefined ? data.avatarUrl || null : avatarUrl,
    cardImageUrl: data.cardImageUrl || imgUrl || null,
    coverImageUrl: data.coverImageUrl !== undefined ? data.coverImageUrl || null : coverUrl,
    cardOcrText: ocrData?.text || data.cardOcrText || null,
  });

  const onSubmit = (data: ProfileSubmit) => {
    const payload = buildPayload(data);
    if (payload.graduateYear != null && !Number.isFinite(payload.graduateYear)) {
      toast.error('畢業年份格式不正確');
      return;
    }
    if (!payload.name) {
      toast.error('請填寫姓名');
      return;
    }
    saveCard.mutate(payload);
  };

  /** 首次導覽：第一階段只驗證姓名後進入名片階段 */
  const goToCardPhase = async () => {
    const ok = await form.trigger(['name', 'email', 'phone', 'graduateYear']);
    if (!ok) {
      toast.error('請先完成必填個人資料（姓名）');
      return;
    }
    setPhase('card');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isAdminEdit && !authUser?.isOwner) {
    return (
      <div className="page-shell text-center py-16">
        <p className="text-red-600 mb-4">僅主管理者可代編名片</p>
        <Link to="/admin" className="btn-primary">
          返回審核
        </Link>
      </div>
    );
  }

  if (profileLoading || !hydrated) {
    return (
      <div className="page-shell flex flex-col items-center justify-center py-20">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-sm text-gray-500">
          {isAdminEdit ? '載入校友資料…' : '載入您的名片資料…'}
        </p>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="page-shell text-center py-16">
        <p className="text-red-600 mb-4">
          {profileErr instanceof Error ? profileErr.message : '無法載入名片，請重新登入'}
        </p>
        <Link to="/login" className="btn-primary">
          重新登入
        </Link>
      </div>
    );
  }

  const showWizard = !isReturningEdit && phase !== 'saved';
  const showPersonal = isReturningEdit || phase === 'personal' || phase === 'card';
  const showCard = isReturningEdit || phase === 'card';

  return (
    <div className="page-shell max-w-3xl mx-auto">
      <div className="mb-6 text-center sm:text-left">
        {isAdminEdit && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-alumni-600 mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            返回
          </button>
        )}
        <h1 className="page-title">
          {isAdminEdit ? (
            <span className="inline-flex items-center gap-2">
              <Shield className="w-7 h-7 text-amber-600" />
              代編校友名片
            </span>
          ) : isReturningEdit ? (
            '編輯我的名片'
          ) : (
            '歡迎加入 · 建立名片'
          )}
        </h1>
        <p className="page-subtitle mx-auto sm:mx-0">
          {isAdminEdit
            ? `正在編輯：${myProfile?.name || '校友'}，更新後請記得按儲存。`
            : isReturningEdit
              ? '歡迎您使用名片編輯功能，更新資訊後請記得按資料儲存，謝謝。'
              : '歡迎建立您的校友名片，請依序完成資料填寫。'}
        </p>
      </div>

      {/* 進度條：首次導覽 */}
      {showWizard && (
        <div className="mb-8 flex items-center justify-center gap-3 sm:gap-4">
          {(
            [
              { key: 'personal' as const, label: '個人資料' },
              { key: 'card' as const, label: '個人名片' },
            ] as const
          ).map((s, i) => (
            <React.Fragment key={s.key}>
              <div
                className={`flex items-center gap-2 ${
                  phase === s.key || (phase === 'card' && s.key === 'personal')
                    ? 'text-alumni-600'
                    : 'text-gray-400'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                    phase === s.key || (phase === 'card' && s.key === 'personal')
                      ? 'bg-alumni-600 border-alumni-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-400'
                  }`}
                >
                  {i + 1}
                </div>
                <span className="hidden sm:block text-sm font-medium">{s.label}</span>
              </div>
              {i === 0 && (
                <div
                  className={`w-12 sm:w-16 h-0.5 rounded ${
                    phase === 'card' ? 'bg-alumni-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {phase === 'saved' ? (
        <div className="card p-10 text-center animate-fade-in">
          <div className="mx-auto w-16 h-16 rounded-full bg-alumni-100 flex items-center justify-center mb-4">
            <CheckCircle className="w-10 h-10 text-alumni-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">儲存成功</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {isAdminEdit
              ? '資料已更新。'
              : lastSaveNeedsReview
                ? '資料已儲存，審核通過後會出現在校友名冊。'
                : '資料已儲存，謝謝。'}
          </p>
          {!imgUrl && !isAdminEdit && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-100 text-left">
              <p className="font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4 shrink-0" />
                您尚未上傳名片或照片
              </p>
              <p className="mt-1 text-amber-800/90 dark:text-amber-200/90">
                可點「繼續編輯」補上，方便校友互相認識。
              </p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to={isAdminEdit ? `/directory/${routeUserId}` : '/directory'}
              className="btn-primary"
            >
              {isAdminEdit ? '查看公開頁' : '前往校友名冊'}
            </Link>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setPhase('personal');
                setIsReturningEdit(true);
              }}
            >
              繼續編輯
            </button>
          </div>
        </div>
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 animate-slide-up">
            {/* ===== 第一階段：個人封面、頭像、資料 ===== */}
            {showPersonal && (isReturningEdit || phase === 'personal') && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-alumni-100 dark:border-alumni-800/40 bg-alumni-50/40 dark:bg-alumni-900/15 px-4 py-3 flex gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 text-alumni-600" />
                  <p>
                    <strong>第一階段 · 個人形象與基本資料</strong>
                    <br />
                    請設定封面、頭像與基本資料。姓名為必填，其餘可稍後再補。
                  </p>
                </div>

                {/* 封面 */}
                <div className="card overflow-hidden">
                  <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700">
                    <SectionHeader
                      step="1-1"
                      title="個人封面照"
                      subtitle="顯示於個人頁頂部；不上傳則使用預設封面。"
                      icon={Image}
                    />
                  </div>
                  <div className="w-full aspect-[21/9] bg-gray-100 dark:bg-gray-800">
                    <ZoomableImage
                      src={coverUrl || '/cover-default.jpg'}
                      alt="公開頁封面預覽"
                      onOpen={(src, alt) => setLightbox({ src, alt })}
                      className="w-full h-full"
                      imgClassName="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 flex flex-wrap gap-2">
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleCoverChange}
                      className="hidden"
                      id="cover-upload"
                      disabled={coverUploading}
                    />
                    <label
                      htmlFor="cover-upload"
                      className={`btn-secondary btn-sm cursor-pointer inline-flex items-center gap-1 ${
                        coverUploading ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      {coverUploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {coverUploading ? '上傳中…' : coverUrl ? '更換封面' : '上傳封面'}
                    </label>
                    {coverUrl && (
                      <button
                        type="button"
                        className="btn-ghost btn-sm text-gray-600"
                        onClick={clearCover}
                        disabled={coverUploading}
                      >
                        還原預設
                      </button>
                    )}
                  </div>
                </div>

                {/* 頭像 */}
                <div className="card p-4 sm:p-5">
                  <SectionHeader
                    step="1-2"
                    title="個人頭像"
                    subtitle="顯示於名冊與個人頁；可使用 LINE 頭像或自行上傳。"
                    icon={User}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="mx-auto sm:mx-0 shrink-0">
                      <div className="w-28 h-28 rounded-2xl overflow-hidden border-2 border-gray-100 dark:border-gray-700 shadow-sm bg-gray-100 dark:bg-gray-800">
                        {avatarUrl ? (
                          <ZoomableImage
                            src={avatarUrl}
                            alt="個人頭像預覽"
                            onOpen={(src, alt) => setLightbox({ src, alt })}
                            className="w-full h-full"
                            imgClassName="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-hero-band">
                            <span className="text-4xl font-bold text-white">
                              {(form.watch('name') || '?').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 text-center sm:text-left">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {avatarUrl
                          ? /line-scdn\.net|profile\.line-cdn\.net/i.test(avatarUrl)
                            ? '目前使用 LINE 頭像，也可改成自己的照片'
                            : '已設定頭像，儲存後生效'
                          : '尚未設定頭像'}
                      </p>
                      <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-2">
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleAvatarChange}
                          className="hidden"
                          id="avatar-upload"
                          disabled={avatarUploading}
                        />
                        <label
                          htmlFor="avatar-upload"
                          className={`btn-secondary btn-sm cursor-pointer inline-flex items-center gap-1 ${
                            avatarUploading ? 'opacity-50 pointer-events-none' : ''
                          }`}
                        >
                          {avatarUploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4" />
                          )}
                          {avatarUploading ? '上傳中…' : avatarUrl ? '更換頭像' : '上傳頭像'}
                        </label>
                        {avatarUrl && (
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-gray-600"
                            onClick={clearAvatar}
                            disabled={avatarUploading}
                          >
                            清除頭像
                          </button>
                        )}
                        {lineAvatarUrl && avatarUrl !== lineAvatarUrl && (
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-alumni-600"
                            onClick={restoreLineAvatar}
                            disabled={avatarUploading}
                          >
                            還原 LINE 頭像
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 個人資料 */}
                <div className="card p-5 sm:p-6 space-y-5">
                  <SectionHeader
                    step="1-3"
                    title="個人資料"
                    subtitle="請填寫姓名與基本資料，姓名為必填。"
                    icon={User}
                  />

                  <div className="rounded-xl border border-alumni-100 dark:border-alumni-800/50 bg-alumni-50/50 dark:bg-alumni-900/20 px-4 py-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="isFaculty"
                        {...form.register('isFaculty')}
                        className="mt-0.5 w-5 h-5 rounded border-gray-300 text-alumni-600 focus:ring-alumni-600"
                      />
                      <label
                        htmlFor="isFaculty"
                        className="text-sm text-gray-800 dark:text-gray-200 cursor-pointer"
                      >
                        <span className="font-medium">我是師長／教職員身分</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
                          勾選後畢業年份可不填。
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="姓名"
                      name="name"
                      required
                      error={form.formState.errors.name}
                    />
                    <Field
                      label={form.watch('isFaculty') ? '系所／單位' : '科系'}
                      name="department"
                      error={form.formState.errors.department}
                      placeholder="例如：資訊管理系"
                    />
                    <GraduateYearField
                      error={form.formState.errors.graduateYear}
                      isFaculty={!!form.watch('isFaculty')}
                    />
                    <Field
                      label="LINE ID"
                      name="lineId"
                      error={form.formState.errors.lineId}
                      hint="選填"
                      placeholder="您的 LINE ID"
                    />
                    <Field
                      label="手機"
                      name="phone"
                      type="tel"
                      error={form.formState.errors.phone}
                      hint="不會公開顯示"
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      error={form.formState.errors.email}
                      hint="不會公開顯示"
                    />
                  </div>

                  <div>
                    <label className="label flex items-center gap-1.5">
                      <Briefcase className="w-4 h-4 text-alumni-600" />
                      產業／產品關鍵字
                    </label>
                    <input
                      {...form.register('industry')}
                      className="input"
                      placeholder="例如：半導體、餐飲、軟體、教育、建材…"
                      maxLength={200}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      選填，可填多個關鍵字
                    </p>
                  </div>

                  <div>
                    <label className="label flex items-center gap-1.5">
                      <HandHelping className="w-4 h-4 text-alumni-600" />
                      我需要的東西／尋求資源
                    </label>
                    <textarea
                      {...form.register('lookingFor')}
                      rows={2}
                      className="input"
                      placeholder="例如：尋找南部通路、需要設計合作、想找同業交流…"
                      maxLength={300}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      選填，寫下您目前需要的協助或資源
                    </p>
                  </div>

                  <div>
                    <label className="label">自我介紹</label>
                    <textarea
                      {...form.register('bio')}
                      rows={3}
                      className="input"
                      placeholder="專長、興趣、想對校友說的話…"
                    />
                    {form.formState.errors.bio && (
                      <p className="text-red-500 text-sm mt-1" role="alert">
                        {form.formState.errors.bio.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isPublic"
                      {...form.register('isPublic')}
                      className="w-5 h-5 rounded border-gray-300 text-alumni-600 focus:ring-alumni-600"
                    />
                    <label htmlFor="isPublic" className="text-sm text-gray-700 dark:text-gray-300">
                      公開於校友名冊
                    </label>
                  </div>
                </div>

                {showWizard && phase === 'personal' && (
                  <div className="flex justify-end gap-3">
                    <button type="button" className="btn-primary" onClick={goToCardPhase}>
                      下一步
                      <Sparkles className="w-4 h-4 ml-1.5" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ===== 第二階段：名片 ===== */}
            {showCard && (
              <div className="space-y-6">
                {(isReturningEdit || phase === 'card') && (
                  <div className="rounded-2xl border border-amber-100 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-900/15 px-4 py-3 flex gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                    <p>
                      <strong>第二階段 · 個人名片</strong>
                      <br />
                      請上傳您的實體名片照片，照片建議清晰資訊可視。
                    </p>
                  </div>
                )}

                <div className="card overflow-hidden">
                  <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700">
                    <SectionHeader
                      step="2.1"
                      title="名片或照片"
                      subtitle="支援 JPG／PNG／WebP；會自動壓縮。"
                      icon={CreditCard}
                    />
                  </div>

                  {imgUrl ? (
                    <>
                      <ZoomableImage
                        src={imgUrl}
                        alt="名片預覽"
                        onOpen={(src, alt) => setLightbox({ src, alt })}
                        className="w-full aspect-[16/10] bg-gray-100 dark:bg-gray-800"
                        imgClassName="w-full h-full object-contain"
                      />
                      <div className="p-3 flex justify-between items-center border-t border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-alumni-600" /> 點擊可放大
                        </span>
                        <label
                          htmlFor="card-upload"
                          className="text-sm text-alumni-600 cursor-pointer inline-flex items-center gap-1"
                        >
                          <RefreshCw className="w-4 h-4" /> 重新上傳
                        </label>
                      </div>
                    </>
                  ) : (
                    <label
                      htmlFor="card-upload"
                      className={`cursor-pointer border-2 border-dashed m-4 rounded-2xl p-10 text-center transition-all duration-200 block ${
                        uploading
                          ? 'border-gray-300 dark:border-gray-600 opacity-50'
                          : 'border-alumni-600 hover:border-alumni-400 hover:bg-alumni-50'
                      }`}
                    >
                      <div className="mx-auto w-16 h-16 rounded-2xl bg-alumni-100 flex items-center justify-center mb-4">
                        <Upload className="w-8 h-8 text-alumni-600" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {uploading ? '上傳辨識中...' : '點擊上傳名片照片'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        長邊約 1600px、約 800KB 以內自動壓縮
                      </p>
                    </label>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                    id="card-upload"
                    disabled={uploading}
                  />

                  {uploading && (
                    <div className="pb-6 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-alumni-600 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400">
                        {ocrLoading ? '正在處理圖片...' : '處理中...'}
                      </p>
                    </div>
                  )}
                </div>

                {ocrData?.text && (
                  <details className="card">
                    <summary className="px-4 py-3 cursor-pointer flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        照片辨識文字（選看）
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowOcrRaw(!showOcrRaw);
                        }}
                        className="text-sm text-alumni-600 flex items-center gap-1"
                      >
                        {showOcrRaw ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        {showOcrRaw ? '收起' : '展開'}
                      </button>
                    </summary>
                    {showOcrRaw && (
                      <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                        <pre className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto max-h-64">
                          {ocrData.text}
                        </pre>
                      </div>
                    )}
                  </details>
                )}

                <div className="card p-5 sm:p-6 space-y-5">
                  <SectionHeader
                    step="2-2"
                    title="個人職涯資訊"
                    subtitle="請輸入您想分享的服務單位名稱與職稱，輸入後再按儲存即可生效。"
                    icon={Briefcase}
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label={form.watch('isFaculty') ? '任職單位／機構' : '公司／機構'}
                      name="currentCompany"
                      error={form.formState.errors.currentCompany}
                      placeholder="目前服務單位"
                    />
                    <Field
                      label="職稱"
                      name="currentTitle"
                      error={form.formState.errors.currentTitle}
                      placeholder="例如：專案經理"
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 justify-between">
                  {showWizard && phase === 'card' && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setPhase('personal')}
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      上一步
                    </button>
                  )}
                  <div className="flex gap-3 sm:ml-auto">
                    <button type="submit" className="btn-primary" disabled={saveCard.isPending}>
                      {saveCard.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" /> 儲存中...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {isAdminEdit ? '儲存代編' : '儲存名片'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 編輯模式：底部固定儲存（當兩階段都顯示時已在 card 區有按鈕） */}
            {isReturningEdit && !showCard && (
              <div className="flex justify-end">
                <button type="submit" className="btn-primary" disabled={saveCard.isPending}>
                  {saveCard.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> 儲存中...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" /> 儲存名片
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </FormProvider>
      )}

      <ImageLightbox
        open={!!lightbox}
        src={lightbox?.src || ''}
        alt={lightbox?.alt}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
}
